import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';
import Img from 'gatsby-image';

import { hasCallToAction, makeImagePreviewable } from '../utils';

import Cell from './Cell';
import Grid from './Grid';
import Block from './Block';
import CTAAnchor from './CTAAnchor';
import H3 from './H3';
import P from './P';
import RichTextHtml from './RichTextHtml';

const HeroVideoWrapper = styled.div`
  position: relative;
  overflow: hidden;
  max-height: 900px;
  min-height: 450px;
`;

const AbsoluteFill = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const Darkener = styled.div`
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.4);
`;

const AspectRatio16x9 = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 145%;

  ${breakpoint('smallTablet')`
    padding-bottom: 80%;
  `};
  ${breakpoint('tablet')`
    padding-bottom: 60%;
  `};
  ${breakpoint('desktop')`
    padding-bottom: 45%;
  `};
  ${breakpoint('large')`
    padding-bottom: 35%;
  `};
`;

const Video = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  z-index: -1;
  transform: translate(-50%, -50%);
`;

const HeroImage = styled(Img)`
  z-index: -1;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PageHero = ({ content }) => {
  const image = makeImagePreviewable(content.image);

  return (
    <HeroVideoWrapper id={content.internal_link_id}>
      <AspectRatio16x9>
        <AbsoluteFill>
          {content.no_overlay ? null : <Darkener />}
          {content.video.url ? (
            <Video playsInline autoPlay muted="muted" loop="loop">
              <source src={content.video.url} type="video/mp4" />
            </Video>
          ) : null}

          {image.localFile && !content.video.url ? (
            <HeroImage
              fluid={image.localFile.childImageSharp.fluid}
              alt={image.alt}
              style={{ position: 'absolute' }}
            />
          ) : null}
        </AbsoluteFill>
      </AspectRatio16x9>

      <AbsoluteFill>
        <Grid
          height="100%"
          valign="bottom"
          pb={{ mobile: 'baseUnit', smallTablet: 'baseUp4' }}
          pt={{
            mobile: 'baseUp12',
            smallTablet: 'baseUp16',
            tablet: 'baseUp20',
          }}
          flexDirection={{ mobile: 'row-reverse' }}
        >
          <Cell
            size={{ mobile: 1, tablet: 1 / 2 }}
            p={{
              mobile: 'baseUp4',
              smallTablet: 'baseUp8',
              tablet: 'baseUnit',
            }}
          >
            <H3
              fontSize={{ mobile: 'baseUp2', smallTablet: 'baseUp4' }}
              color="white"
              mb="baseUnit"
            >
              {content.headline.text}
            </H3>

            <RichTextHtml html={content.subtitle.html} color="white" />
            <P color="coral" mb="0">
              {content.coral_subtitle.text}
            </P>
          </Cell>
          <Cell
            size={{ mobile: 1, tablet: 1 / 2 }}
            style={{ textAlign: 'center' }}
            pb="baseUnit"
          >
            {hasCallToAction(content) ? (
              <Block pt="baseUp4">
                <CTAAnchor
                  text={content.call_to_action_text.text}
                  link={content.call_to_action_link}
                  colorTheme={content.call_to_action_color}
                />
              </Block>
            ) : null}
          </Cell>
        </Grid>
      </AbsoluteFill>
    </HeroVideoWrapper>
  );
};

PageHero.propTypes = {
  content: PropTypes.shape({
    internal_link_id: PropTypes.string,
    headline: PropTypes.shape({ text: PropTypes.string }),
    subtitle: PropTypes.shape({ html: PropTypes.string }),
    coral_subtitle: PropTypes.shape({ text: PropTypes.string }),
    video: PropTypes.shape({ url: PropTypes.string }),
    image: PropTypes.shape({
      alt: PropTypes.string,
      localFile: PropTypes.shape({
        childImageSharp: PropTypes.shape({ fluid: PropTypes.shape({}) }),
      }),
    }),
    call_to_action_color: PropTypes.string,
    call_to_action_text: PropTypes.shape({ text: PropTypes.string }),
    call_to_action_link: PropTypes.shape({
      url: PropTypes.string,
      link_type: PropTypes.string,
      lang: PropTypes.string,
      target: PropTypes.string,
    }),
    no_overlay: PropTypes.bool,
  }).isRequired,
};

export default PageHero;

export const query = graphql`
  fragment PageHeroFragment on PrismicPageBodyHero {
    id
    slice_type
    primary {
      internal_link_id
      headline {
        text
      }
      subtitle {
        html
      }
      coral_subtitle {
        text
      }
      video {
        url
      }
      call_to_action_color
      call_to_action_text {
        text
      }
      call_to_action_link {
        link_type
        url
        lang
        target
      }
      no_overlay
      image {
        alt
        localFile {
          childImageSharp {
            fluid(maxWidth: 2500, maxHeight: 1200) {
              ...GatsbyImageSharpFluid
            }
          }
        }
      }
    }
  }
`;
